import React, { createContext, useCallback, useContext, useMemo, useState } from "react";

export const COLOR_MODE_STORAGE_KEY = "chinookdt-color-mode";

const getInitialColorMode = () => {
  if (typeof window === "undefined") {
    return "light";
  }

  const stored = localStorage.getItem(COLOR_MODE_STORAGE_KEY);
  if (stored === "light" || stored === "dark") {
    return stored;
  }

  return window.matchMedia("(prefers-color-scheme: dark)").matches
    ? "dark"
    : "light";
};

const StyleContext = createContext({
  sideNavOpen: false,
  setSideNavOpen: () => {},
  colorMode: "light",
  toggleColorMode: () => {},
  setColorMode: () => {},
});

function StyleContextProvider({ children }) {
  const [sideNavOpen, setSideNav] = useState();
  const [colorMode, setColorModeState] = useState(getInitialColorMode);

  const setSideNavOpen = useCallback((open) => {
    setSideNav(open);
  }, []);

  const setColorMode = useCallback((mode) => {
    if (mode !== "light" && mode !== "dark") {
      return;
    }
    setColorModeState(mode);
  }, []);

  const toggleColorMode = useCallback(() => {
    setColorModeState((prev) => (prev === "dark" ? "light" : "dark"));
  }, []);

  const value = useMemo(
    () => ({
      sideNavOpen,
      setSideNavOpen,
      colorMode,
      toggleColorMode,
      setColorMode,
    }),
    [sideNavOpen, setSideNavOpen, colorMode, toggleColorMode, setColorMode]
  );

  return (
    <StyleContext.Provider value={value}>{children}</StyleContext.Provider>
  );
}

export function useStyleContext() {
  return useContext(StyleContext);
}

export { StyleContextProvider, StyleContext };
